import { ReactPlugin, withAITracking } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights, DistributedTracingModes, SeverityLevel } from '@microsoft/applicationinsights-web';
import get from 'lodash-es/get';
import { store } from '../store/configureStore';
import { maskPIIProperties } from './personallyIdentifiableInformation/piiUtils';
import { getCheckinDateSelection, getCheckoutDateSelection } from '../store/componentStores/Dates/dates.selectors';
import { getAdultsCount, getKidsAges } from '../store/componentStores/Guests/guests.selectors';
import { getOfferCode } from '../store/componentStores/Offer/offer.selectors';
import { getResortLocation } from '../store/componentStores/CurrentLodge/currentLodge.selectors';
import { getSelectedSuiteSuiteCode } from '../store/componentStores/Suite/suite.selectors';

const reactPlugin = new ReactPlugin();

const ai = new ApplicationInsights({
  config: {
    instrumentationKey: process.env.REACT_APP_APPINSIGHTS_INSTRUMENTATION_KEY,
    enableAutoRouteTracking: true,
    extensions: [reactPlugin],
    distributedTracingMode: DistributedTracingModes.W3C,
    enableCorsCorrelation: true,
    correlationHeaderDomains: ['*.greatwolf.com'],
    disableAjaxTracking: true,
    disableFetchTracking: true
  }
});

ai.addTelemetryInitializer(function(envelope) {
  envelope = maskPIIProperties(envelope);
});

ai.loadAppInsights();

const appInsights = ai.appInsights;

export const trackInteractionFailureEvent = (errorMessage, eventName) => {
  trackError(eventName, errorMessage);
};

/**
 * @param {String} eventName
 * @param {String} errorMessage
 * @param {Object} errorObject
 */
export const trackReservationFailureEvent = (eventName, errorMessage, errorObject) => {
  // We stringify so we force the Error object to materialize all of it's properties
  // Then we need to parse it to a JSON object so maskPIIProperties knows how to traverse and mask it's PII properties
  const stringfiedErrorObject = JSON.stringify(errorObject);
  errorObject = maskPIIProperties(JSON.parse(stringfiedErrorObject));
  trackError(eventName, `${errorMessage} Error: ${JSON.stringify(errorObject)}`);
};

/**
 * Track I4Iframe error or event
 * @param {String} eventName
 * @param {Object} properties
 */
export const trackI4IframeAppInsightsEvent = (eventName, properties) => {
  try {
    if (eventName === EVENT_CATEGORIES.onI4IframeError) {
      trackError(EVENT_CATEGORIES.onI4IframeError, JSON.stringify(properties));
    } else {
      appInsights.trackEvent({ name: eventName, properties });
    }
  } catch (error) {
    console.error('Error tracking I4Iframe event:', error);
  }
};

/**
 * Track error on availability, highest rates and detailed availability
 * @param {Object} state Redux state object
 * @param {String} errorTitle Error title shown on availability error
 */
export const availabilityTrackError = (state, errorType, errorTitle, isDetailedAvailabilty = false) => {
  const errorMessage = !isDetailedAvailabilty
    ? get(state, 'availabilityErrorMessage', '')
    : get(state, 'suiteAvailabilityErrorMessage', '');
  trackPlanPageFailureEvent(errorType, errorTitle, errorMessage, isDetailedAvailabilty);
};

export const trackPlanPageFailureEvent = (errorType, errorTitle, errorMessage, isDetailedAvailabilty) => {
  // fail silently
  try {
    const { dates, guests, offer, currentLodge, suite } = store.getState();

    // Gather information from user's selection
    let userSelection = {
      arrival: getCheckinDateSelection(dates),
      departure: getCheckoutDateSelection(dates),
      adultCount: getAdultsCount(guests),
      kidsAges: getKidsAges(guests),
      offerCode: getOfferCode(offer),
      property: getResortLocation(currentLodge)
    };

    if (isDetailedAvailabilty) {
      userSelection.suiteCode = getSelectedSuiteSuiteCode(suite);
    }

    const userChoice = JSON.stringify(userSelection);
    const errorLogInfo = `Error on plan page, ${errorTitle}. ${errorMessage};`;

    appInsights.trackException({
      exception: new Error(errorLogInfo),
      severityLevel: SeverityLevel.Error,
      properties: {
        component: 'BookingEngine',
        method: errorType,
        message: userChoice
      }
    });
  } catch (error) {}
};

export const trackError = (eventName, errorMessage) => {
  try {
    appInsights.trackEvent({
      name: eventName,
      properties: { error: errorMessage }
    });
  } catch (error) {}
};

export const trackVariationJEventError = (eventName, trackInformation) => {
  try {
    appInsights.trackEvent({
      name: eventName,
      properties: {
        lodge: trackInformation.lodge,
        dates: trackInformation.dates,
        promoCode: trackInformation.promoCode,
        partySize: trackInformation.partySize,
        californiaResidentFlag: trackInformation.californiaResidentFlag,
        rateCodes: trackInformation.rateCodes,
        suiteStyle: trackInformation.suiteStyle
      }
    });
  } catch (error) {}
};

export const trackVariationHEventError = (eventName, trackInformation) => {
  try {
    appInsights.trackEvent({
      name: eventName,
      properties: {
        lodge: trackInformation.lodge,
        dates: trackInformation.dates,
        promoCode: trackInformation.promoCode,
        partySize: trackInformation.partySize,
        suiteStyle: trackInformation.suiteStyle,
        savings: trackInformation.savings
      }
    });
  } catch (error) {}
};

export const EVENT_CATEGORIES = {
  bookingEngineBookReservationError: 'OnBookReservationError',
  OnVersionJEventError: 'OnVersionJEventError',
  OnVersionHEventError: 'OnVersionHEventError',
  onI4IframeError: 'OnI4IframeError',
  on3DSChallengeModalOpen: 'On3DSChallengeModalOpen',
  on3DSChallengeModalCancel: 'On3DSChallengeModalCancel',
  on3DSPaymentPageLoaded: 'On3DSPaymentPageLoaded',
  on3DSPaymentSectionLoaded: 'On3DSPaymentSectionLoaded',
  on3DSBackendHandling: 'On3DSBackendHandling',
  on3DSIFrameInitialized: 'On3DSIFrameInitialized',
  on3DSPaymentTypeSelected: 'On3DSPaymentTypeSelected',
  on3DSBookingSuccess: 'On3DSBookingSuccess'
};

const withAppInsights = Component => withAITracking(reactPlugin, Component);

export default withAppInsights;
